import { EyeIcon, SendHorizontalIcon } from 'lucide-react'

import { AssessmentDueDatePicker } from '@/pages/assessment/components/assessment-due-date-picker'

import { Button } from '@/components/lemmala-design-system/button/button'
import { Hyperlink } from '@/components/lemmala-design-system/link/hyperlink'
import { Separator } from '@/components/lemmala-design-system/separator/separator'
import { Tooltip } from '@/components/lemmala-design-system/tooltip/tooltip'

import { RecipientsButton } from './recipients-button'
import { SmartEvidenceRequestBannerLayout } from './smart-evidence-request-banner-layout'
import type { SmartEvidenceRequestBannerProps } from './types'
import { useSmartEvidenceRequest } from './use-smart-evidence-request.hook'

export const SmartEvidenceRequestDraftBanner = (props: SmartEvidenceRequestBannerProps) => {
  const { company, smartEvidenceQuestionnaire, assessmentId, dueDate, readonly } = props
  const companyName = company.profile?.name ?? ''
  const {
    isUpdating,
    getPreviewUrl,
    handleTrackPreview,
    handlePublish,
    InviteRecipientModal,
    openInviteRecipientModal,
  } = useSmartEvidenceRequest(props)

  return (
    <SmartEvidenceRequestBannerLayout
      title='Smart Evidence Request'
      inviteRecipientModal={!readonly && InviteRecipientModal()}
      description={`Share unvalidated controls with ${companyName}`}
      recipientsButton={
        <div className='flex items-center gap-2'>
          <RecipientsButton
            recipientsEmails={company.recipientsEmails}
            onClick={openInviteRecipientModal}
            readonly={readonly}
          />
          <Separator orientation='vertical' className='h-5' />
          <AssessmentDueDatePicker
            assessmentId={assessmentId}
            dueDate={dueDate}
            readonly={readonly}
          />
        </div>
      }
      secondaryAction={
        !readonly && (
          <Tooltip
            trigger={
              <Hyperlink target='_blank' href={getPreviewUrl()} onClick={handleTrackPreview}>
                <Button
                  variant='ghost'
                  size='icon-sm'
                  disabled={!smartEvidenceQuestionnaire}
                  className='size-6'
                >
                  <EyeIcon className='size-4' />
                </Button>
              </Hyperlink>
            }
          >
            Preview smart evidence request
          </Tooltip>
        )
      }
      primaryAction={
        <Tooltip
          tooltipDisabled={!readonly}
          trigger={
            <span className='inline-flex'>
              <Button
                onClick={handlePublish}
                disabled={readonly || isUpdating || !smartEvidenceQuestionnaire}
              >
                Share
                <SendHorizontalIcon className='ml-2 size-4' />
              </Button>
            </span>
          }
        >
          {readonly && 'You do not have permission to publish'}
        </Tooltip>
      }
    />
  )
}
